import { jsxs, jsx } from 'react/jsx-runtime';
import { Component } from 'react';
import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';
import noop from 'lodash/noop';
import { Superscript } from './Items/Superscript.js';
import { SelectBox } from './Items/SelectBox.js';
import LongInstruction from './Items/LongInstruction.js';
import Options from './Items/Options.js';
import { Popover } from '../Popover/index.js';
import { SelectElement } from './SelectNativeElements/SelectElement.js';
import DefaultDisplayLabel from './SelectNativeElements/DefaultDisplayLabel.js';
import { SelectLabel } from './SelectNativeElements/SelectLabel.js';
import { getValueObject, setDefaultValue, constructLabelWithDescriptor } from './SelectNativeElements/native-utils.js';

const renderValue = (valueRenderer, val) => valueRenderer && typeof valueRenderer === 'function' ? valueRenderer(val) : null; // TODO: fix eslint rule react/no-unsafe https://git.target.com/design-systems/nicollet/issues/1315

/* eslint-disable react/no-unsafe */


class SelectCustom extends Component {
  constructor(props) {
    super(props);
    this.selectBoxRef = null;
    this.optionsRef = null;
    this.wrapper = null;
    const {
      label,
      boldLabel,
      defaultValue,
      boldValue,
      imageUrl,
      target,
      isOpen
    } = props;
    this.state = {
      boldLabel,
      boldValue,
      imageUrl,
      isOpen,
      label,
      selected: [],
      target,
      value: defaultValue
    };
    this.toggleMenu = this.toggleMenu.bind(this);
    this.onChange = this.onChange.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.close = this.close.bind(this);
    this.setSelectBoxRef = this.setSelectBoxRef.bind(this);
    this.setOptionsRef = this.setOptionsRef.bind(this);
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.onChangeNative = this.onChangeNative.bind(this);
  }
  /* document is defined, but the linter doesn't think so, hence the rule below */

  /* eslint-disable no-undef */

  /* eslint-disable-next-line camelcase */


  UNSAFE_componentWillMount() {
    if (typeof window === 'undefined') return; // @ts-expect-error TODO: figure out how to type handleClickOutside

    document.addEventListener('click', this.handleClickOutside);
    this.setSelected();

    if (!this.state.target) {
      this.setState({
        target: uniqueId('select-')
      });
    }
  }
  /**
   * On subsequent updates after first mount
   * dropdown will not update unless updated in state.
   */

  /* eslint-disable-next-line camelcase */


  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      autoClose,
      options,
      label,
      imageUrl,
      value
    } = nextProps;
    const {
      options: oldOptions,
      value: oldValue
    } = this.props;

    if (options && !isEqual(options, oldOptions) || oldValue !== value) {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const defaultValue = value || nextProps.defaultValue || this.props.defaultValue;
      this.setState({
        selected: [],
        value: defaultValue
      });
    }

    this.setSelected(nextProps); // only if autoClose is off, the container will get
    // the control of closing the dropdown when needed

    if (!autoClose && !isEqual(oldValue, value)) {
      this.setState({
        isOpen: true
      });
    }

    this.setState({
      label,
      imageUrl
    });
  }

  componentWillUnmount() {
    // @ts-expect-error TODO: figure out how to type handleClickOutside
    document.removeEventListener('click', this.handleClickOutside);
  }

  onChange(event, data) {
    const {
      autoClose
    } = this.props;
    const selectedOptions = this.selectedValue(data);
    this.props.onChange(event, selectedOptions);

    if (autoClose) {
      this.close(true);
    }
  }
  /**
   *
   * Event handling specifically for the select native
   */


  onChangeNative(event) {
    var _a;

    const {
      options,
      onChange
    } = this.props;
    if (!options) return;
    const selectedOption = options.find(getValueObject((_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.value));

    if (selectedOption) {
      this.selectedValue(selectedOption, options);
    }

    if (onChange) {
      onChange(event, selectedOption);
    }
  }

  setSelectBoxRef(el) {
    this.selectBoxRef = el;
  }

  setOptionsRef(el) {
    this.optionsRef = el;
  }

  setWrapperRef(el) {
    this.wrapper = el;
  }

  setSelected(nextProps) {
    const props = nextProps !== null && nextProps !== void 0 ? nextProps : this.props;
    const {
      multiSelect,
      value,
      options
    } = props;

    if (!value) {
      return;
    }

    if (multiSelect) {
      const selected = Array.isArray(value) ? value : [value];
      const currentSelectedOptions = this.selectedOptions({
        selected
      }, nextProps);
      this.setState({
        selected
      });
      this.updateDisplayLabel(currentSelectedOptions);
    } else {
      this.selectedValue({
        value
      }, options);
    }
  }

  handleClickOutside(event) {
    // Since Options is wrapped in a Popover, we need to prevent clicks that
    // are made there from registering as outside of the Select
    // @ts-expect-error
    const isOptionsClick = this.optionsRef ? this.optionsRef.contains(event.target) : null; // handle touch outside on ios to dismiss menu

    if (this.wrapper && // TODO: figure out how to type handleClickOutside
    // @ts-expect-error
    !this.wrapper.contains(event.target) && this.state.isOpen && !isOptionsClick) {
      this.close();
    }
  }
  /**
   * closes the options menu and sets focus to Select box
   */


  close(setFocus) {
    if (setFocus) {
      this.setState({
        isOpen: false
      }, () => {
        var _a;

        return (_a = this.selectBoxRef) === null || _a === void 0 ? void 0 : _a.focus();
      });
    }

    return null;
  }
  /**
   * Updates the selected values with a new selection
   * @param {object} data the current selected option
   * @param {string} data.label display label
   * @param {string} data.value option value
   * @param {array} nextOptions New options so this can be used in UNSAFE_componentWillReceiveProps
   * @return {Object|Object[]|null} current selected option(s)
   */


  selectedValue(data, nextOptions) {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const options = nextOptions || this.props.options;
    const {
      multiSelect
    } = this.props;
    const {
      value
    } = data; // Don't do anything if the value isn't in the options

    if (!(options === null || options === void 0 ? void 0 : options.map(option => option.value).includes(value))) {
      return null;
    }

    let newSelected = [value];

    if (multiSelect) {
      const {
        selected
      } = this.state;

      if (selected) {
        newSelected = [...selected, value];
      }

      if (value && (selected === null || selected === void 0 ? void 0 : selected.includes(value))) {
        newSelected = selected.filter(option => option !== value);
      }
    }

    const newOptions = options.filter(option => newSelected.includes(option.value));
    this.updateDisplayLabel(newOptions);
    this.setState({
      selected: newSelected
    });

    if (multiSelect) {
      return newOptions;
    }

    return newOptions[0];
  }
  /**
   * on autoLabelUpdate if label is present the
   * the selected option's text gets updated in label
   * else in label
   * @param {Array} nextOptions Array of newly selected options to get label from
   */


  updateDisplayLabel(nextOptions) {
    const {
      autoLabelUpdate = true,
      defaultValue
    } = this.props;
    const selectedOptions = nextOptions || this.selectedOptions();

    if (!autoLabelUpdate) {
      return;
    }

    let newValue = selectedOptions[0] || {
      label: defaultValue
    };

    if (selectedOptions.length > 1) {
      newValue = {
        label: `(${selectedOptions.length})`
      };
    }

    this.setState(() => ({
      value: newValue.label,
      imageUrl: newValue.imageUrl,
      descriptor: newValue.descriptor,
      hasStrikethrough: !!newValue.hasStrikethrough
    }));
  }
  /**
   * toggles menu display
   */


  toggleMenu(event) {
    event.preventDefault();
    this.setState(prevState => ({
      isOpen: !prevState.isOpen
    }));
  }

  selectedOptions(nextState, nextProps) {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const {
      options,
      multiSelect
    } = nextProps || this.props; // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing

    const {
      selected
    } = nextState || this.state;
    const selectedOptions = options.filter(option => {
      if (option.value) {
        return selected === null || selected === void 0 ? void 0 : selected.includes(option.value);
      }

      return false;
    });
    return multiSelect ? selectedOptions : selectedOptions[0];
  }

  render() {
    const {
      arrowLabelValue,
      autoClose,
      autoLabelUpdate,
      className,
      customLabelImage,
      dataTestPrefix,
      defaultValue,
      disabled,
      display,
      disabledOptionText,
      errorText,
      flip,
      id,
      imageUrl: propsImageUrl,
      isHidden,
      isOpen: propsIsOpen,
      isValid,
      multiSelect,
      name = id,
      onChange,
      options = [],
      optionType,
      placement,
      renderPopoverInline = false,
      size,
      superscript,
      value: propsValue,
      valueRenderer,
      variant = 'secondary',
      wrapperStyle,
      isNative,
      ...restProps
    } = this.props;
    const {
      boldLabel,
      boldValue,
      descriptor,
      hasStrikethrough,
      imageUrl,
      isOpen,
      label,
      selected,
      target,
      value
    } = this.state;
    const mutatedOptions = options === null || options === void 0 ? void 0 : options.map(option => (selected === null || selected === void 0 ? void 0 : selected.includes(option === null || option === void 0 ? void 0 : option.value)) ? { ...option,
      selected: true
    } : { ...option,
      selected: false
    });
    const isError = isValid === false;
    const isValidValue = isValid === true && value !== '';
    const openModal = typeof window === 'undefined' ? null : document.querySelector('.ReactModal__Content--after-open');
    const optionData = {
      dataTestPrefix,
      childRef: this.setOptionsRef,
      isOpen,
      multiSelect,
      onChange: this.onChange,
      onClose: this.close,
      options: mutatedOptions,
      target,
      type: optionType,
      modalHeight: openModal === null || openModal === void 0 ? void 0 : openModal.clientHeight,
      disabledOptionText,
      modalWidth: openModal ? openModal.clientWidth * 0.8 : 0 // open select at 80% of the width

    };
    const renderedValue = renderValue(valueRenderer, value);
    return jsxs("div", Object.assign({
      className: className,
      ref: this.setWrapperRef,
      style: wrapperStyle
    }, {
      children: [superscript && jsx(Superscript, Object.assign({
        disabled: disabled,
        invalid: isError,
        valid: isValidValue
      }, {
        children: superscript
      }), void 0), isNative ? jsxs(SelectLabel, Object.assign({
        display: display,
        htmlFor: id
      }, restProps, {
        children: [jsxs(SelectElement, Object.assign({
          "data-test": dataTestPrefix,
          disabled: disabled,
          id: id,
          isValid: isValid,
          name: name,
          onChange: this.onChangeNative,
          value: setDefaultValue(options, value),
          variant: variant
        }, restProps, {
          children: [disabledOptionText && jsx("option", Object.assign({
            disabled: true,
            value: ""
          }, {
            children: disabledOptionText
          }), void 0), options.map(({
            value: val,
            label: innerLabel = val,
            descriptor: desc
          }) => val ? jsx("option", Object.assign({
            value: val
          }, {
            children: constructLabelWithDescriptor(innerLabel, desc)
          }), `${innerLabel}-${val}`) : null)]
        }), void 0), jsx(DefaultDisplayLabel, Object.assign({
          boldLabel: boldLabel,
          boldValue: boldValue,
          disabled: disabled,
          display: display,
          invalid: isError,
          label: label,
          size: size,
          valid: isValidValue,
          value: value,
          valueRenderer: renderedValue,
          variant: variant
        }, restProps), void 0)]
      }), void 0) : jsxs("div", {
        children: [jsx(SelectBox, Object.assign({
          "$childRef": this.setSelectBoxRef,
          "aria-expanded": isOpen,
          arrowLabelValue: arrowLabelValue,
          boldLabel: boldLabel,
          boldValue: boldValue,
          customLabelImage: customLabelImage,
          descriptor: descriptor,
          disabled: disabled,
          display: display,
          hasStrikethrough: hasStrikethrough,
          id: target,
          imageUrl: imageUrl,
          invalid: isError,
          isHidden: isHidden,
          isOpen: isOpen,
          label: label,
          size: size,
          toggleMenu: this.toggleMenu,
          valid: isValidValue,
          value: value,
          valueRenderer: renderedValue,
          variant: variant
        }, restProps), void 0), jsx(Popover, Object.assign({
          containerFluid: true,
          "data-test": "@nicollet/SelectCustom/Popover",
          flip: flip,
          isOpen: isOpen,
          placement: placement,
          renderInline: renderPopoverInline,
          target: target,
          toggle: this.close,
          variant: "secondary"
        }, {
          children: jsx(Options, Object.assign({}, optionData), void 0)
        }), void 0)]
      }, void 0), isError && jsx(LongInstruction, Object.assign({
        className: "longInstruction",
        id: `${id}--longInstruction`
      }, {
        children: errorText
      }), void 0)]
    }), void 0);
  }

}
SelectCustom.displayName = 'SelectCustom';
SelectCustom.defaultProps = {
  autoClose: true,
  customLabelImage: true,
  isOpen: false,
  onChange: noop,
  options: [],
  optionType: 'default',
  placement: 'bottom',
  renderPopoverInline: false,
  variant: 'secondary'
};

export { SelectCustom };
